import logo from './logo.svg';
import './App.css';
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

const GRID_SIZE = 5
const WILD_CARD_VALUE = 11;
const CLEAR_VALUE = 100;

const SEVEN_START_CLEARS = 50;
const EIGHT_START_CLEARS = 200;
const NINE_START_CLEARS = 400;
const TEN_START_CLEARS = 600;
const DEFAULT_TILE_WEIGHT = 50;

// expiration date for stat cookies
const EXPIRATION_DATE = new Date(9999, 0, 1);

function App() {
  const defaultCellTile = 0;
  const [grid, setGrid] = useState(
    Array.from({length: GRID_SIZE}, () => new Array(GRID_SIZE).fill(defaultCellTile))
  );
  const [turns, setTurns] = useState(0);
  const [nextTile, setNextTile] = useState(0);
  const [clears, setClears] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [endless, setEndless] = useState(false);
  const [showWin, setShowWin] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [stats, setStats] = useState({
    historicGames: 0,
    historicGameAvg: 0.0,
    historicWins: 0,
    historicWinsAvg: 0.0
  })

  /*
   * 6 cookies:
   * board state
   * next tile
   * turns
   * game over?
   * current average
   * number of runs
   * current best
   */

  const calculateTileWeight = (startClears) => {
    return clears < startClears ? 0 : Math.min(clears - startClears, DEFAULT_TILE_WEIGHT);
  }

  const getRandomTileValue = () => {
    //return Math.floor(Math.random() * MAX_TILE_VALUE) + 1;

    // basic weighted probability algorithm
    let tiles = [1, 2, 3, 4, 5, 6, 7, WILD_CARD_VALUE, 8, 9, 10];
    let weights = new Array(6).fill(DEFAULT_TILE_WEIGHT);
    weights.push(
      calculateTileWeight(SEVEN_START_CLEARS),
      calculateTileWeight(SEVEN_START_CLEARS),
      calculateTileWeight(EIGHT_START_CLEARS),
      calculateTileWeight(NINE_START_CLEARS),
      calculateTileWeight(TEN_START_CLEARS)
    );

    let total_weight = weights.reduce((acc, val) => acc + val, 0);
    let rand = Math.random() * total_weight;
    let t = 0;
    for (let i = 0; i < weights.length; i++) {
      t += weights[i];
      if (t > rand) {
        return tiles[i];
      }
    }
    console.log("SHOULD NOT BE POSSIBLE");
    return 1;
  }

  const restart = () => {
    let newBoard = Array.from({length: GRID_SIZE}, () => new Array(GRID_SIZE).fill(defaultCellTile));
    let newNextTile = 1;

    // reset state
    setTurns(0);
    setClears(0);
    setNextTile(newNextTile);
    setGrid(newBoard);
    setGameOver(false);
    setEndless(false);
    setCopiedToClipboard(false);

    // reset cookies
    Cookies.set("turns", 0, {expires: EXPIRATION_DATE});
    Cookies.set("clears", 0, {expires: EXPIRATION_DATE});
    Cookies.set("nextTile", newNextTile, {expires: EXPIRATION_DATE});
    Cookies.set("board", JSON.stringify(newBoard), {expires: EXPIRATION_DATE});
    Cookies.set("gameOver", 0, {expires: EXPIRATION_DATE});
    Cookies.set("endless", 0, {expires: EXPIRATION_DATE});
  }

  const setCell = (rowIndex, colIndex, val) => {
    setGrid(prevGrid => {
      const newGrid = prevGrid.map((row, rIdx) => {
        if (rIdx === rowIndex) {
          return row.map((cell, cIdx) => (cIdx === colIndex ? val : cell));
        }
        return row;
      });
      return newGrid;
    });
  };

  const startClearTile = (rowIndex, columnIndex, timingOffset) => {
    setTimeout(() => {
      clearTile(rowIndex, columnIndex);
    }, (rowIndex + columnIndex - timingOffset + 3) * 50);
  }
  const clearTile = (rowIndex, columnIndex) => {
    setCell(rowIndex, columnIndex, CLEAR_VALUE);
    setTimeout(() => {
      setCell(rowIndex, columnIndex, 0);
    }, 750);
  }

  const clearRow = (workingGrid, rowIndex) => {
    for (let i = 0; i < GRID_SIZE; i++) {
      workingGrid[rowIndex][i] = 0;
      startClearTile(rowIndex, i, rowIndex);
    } 
  }
  const clearColumn = (workingGrid, colIndex) => {
    for (let i = 0; i < GRID_SIZE; i++) {
      workingGrid[i][colIndex] = 0;
      startClearTile(i, colIndex, colIndex);
    }
  }

  const clearMelds = (workingGrid) => {
    let numClears = 0;
    let readOnlyGrid = structuredClone(workingGrid);
    // check rows
    for (let i = 0; i < GRID_SIZE; i++) {
      let a = readOnlyGrid[i].filter(cell => cell != WILD_CARD_VALUE); // delete wild card
      let s = new Set(a);
      if (s.has(0)) continue;
      if (s.size <= 1) {
        if ((a[0] == 7 || s.size == 0) && !endless) {
          win(); // win
          return false;
        }
        clearRow(workingGrid, i); // 5 of a kind
        numClears += 1;
      }
      if (s.size == a.length && Math.max(...a) - Math.min(...a) <= GRID_SIZE - 1) {
        clearRow(workingGrid, i); // straight
        numClears += 1;
      }
    }
    // check columns
    for (let i = 0; i < GRID_SIZE; i++) {
      let a = readOnlyGrid.map(row => row[i]).filter(cell => cell != WILD_CARD_VALUE); // delete wild card
      let s = new Set(a);
      if (s.has(0)) continue;
      if (s.size <= 1) {
        if ((a[0] == 7 || s.size == 0) && !endless) {
          win(); // win
          return false;
        }
        clearColumn(workingGrid, i); // 5 of a kind
        numClears += 1;
      }
      if (s.size == a.length && Math.max(...a) - Math.min(...a) < GRID_SIZE) {
        clearColumn(workingGrid, i); // straight
        numClears += 1;
      }
    }

    // track clears to know when to add new tiles
    setClears(clears + numClears);
    Cookies.set("clears", clears + numClears, {expires: EXPIRATION_DATE});

    // game is not won
    return true;
  };

  const gameOverCheck = (gridMirror) => {
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        if (gridMirror[i][j] == 0 || gridMirror[i][j] == CLEAR_VALUE) {
          return false;
        }
      }
    }
    lose();
    return true;
  }

  const win = () => {
    console.log("WIN");
    setShowWin(true);
    setEndless(true);
    Cookies.set("endless", 1, {expires: EXPIRATION_DATE});

    // historic stats
    let historicWins = getCookieOrDefault("historicWins", 0);
    let historicWinAvg = getCookieOrDefault("historicWinAvg", 0.0);
    let newTotalWinTurns = (historicWins * historicWinAvg) + turns + 1;
    historicWins += 1;
    historicWinAvg = newTotalWinTurns / historicWins;
    let newStats = {
      historicGames: stats.historicGames,
      historicGameAvg: stats.historicGameAvg,
      historicWins: historicWins,
      historicWinAvg: historicWinAvg
    };
    setStats(newStats);
    Cookies.set("stats", JSON.stringify(newStats), {expires: EXPIRATION_DATE});
  }

  const lose = () => {
    setGameOver(true);

    // historic stats
    let historicGames = stats.historicGames;
    let historicGameAvg = stats.historicGameAvg;
    let newTotalGameTurns = (historicGames * historicGameAvg) + turns + 1;
    historicGames += 1;
    historicGameAvg = newTotalGameTurns / historicGames;
    let newStats = {
      historicGames: historicGames,
      historicGameAvg: historicGameAvg,
      historicWins: stats.historicWins,
      historicWinAvg: stats.historicWinAvg
    };
    setStats(newStats);
    Cookies.set("stats", JSON.stringify(newStats), {expires: EXPIRATION_DATE});
  }

  const startEndless = () => {
    // we don't set endless here--
    // we set endless when the win state is satisfied (in win())
    // so we can run clearMelds here and have it reflect the correct endless state.
    let gridMirror = structuredClone(grid);
    clearMelds(gridMirror);
    setShowWin(false);
    setCopiedToClipboard(false);
  }

  const copyResultString = () => {
    let emojiMap = ['▫️', '⬛', '🟥', '🟨', '🟩', '🟦', '🟪', '⬜', '🟧', '🩷', '🔳', '🟫'];
    let resultString = "";
    resultString += `Turn ${turns}${endless ? '*' : ''}\n`;
    for (let i = 0; i < GRID_SIZE; i++) {
      for (let j = 0; j < GRID_SIZE; j++) {
        resultString += emojiMap[grid[i][j]];
      }
      resultString += '\n';
    }
    resultString += "rumm.io";
    navigator.clipboard.writeText(resultString);
    setCopiedToClipboard(true);
  }

  const placeTile = (row, column) => {
    // In a perfect world, we would directly modify the grid variable.
    // But, setState operations in React are NOT immediately reflected in their corresponding variable--
    // changes to state (such as the grid) are only readable after the next render.
    // To get around this, we create a working copy of the grid, and use that
    // to track any real-time changes to the grid that occur.
    let workingGrid = structuredClone(grid);
    if (!(workingGrid[row][column])) {
      // advance turn
      setTurns(turns + 1);
      Cookies.set("turns", turns + 1, {expires: EXPIRATION_DATE});

      // place tile
      workingGrid[row][column] = nextTile;
      setCell(row, column, nextTile);

      // clear melds
      if (!clearMelds(workingGrid)) return;
      Cookies.set("board", JSON.stringify(workingGrid), {expires: EXPIRATION_DATE});

      // choose next tile
      let nextTileValue = getRandomTileValue();
      setNextTile(nextTileValue);
      Cookies.set("nextTile", nextTileValue, {expires: EXPIRATION_DATE});

      // game over check
      let isGameOver = gameOverCheck(workingGrid);
      Cookies.set("gameOver", isGameOver ? 1 : 0, {expires: EXPIRATION_DATE});
    }
  };

  const getCookieOrDefault = (name, d) => {
    // have to use ? : instead of ?? because of casting issues
    if (typeof d == "number") {
      return Cookies.get(name) ? parseFloat(Cookies.get(name)) : d;
    }
    if (typeof d == "boolean") {
      return Cookies.get(name) !== undefined ? Cookies.get(name) == 1 : d;
    }
  }

  const tileDisplayText = (cell) => {
    if (cell == 0 || cell == WILD_CARD_VALUE) return "";
    if (cell == 10) return "X";
    return cell;
  }

  useEffect(() => {
    if (Cookies.get("board")) {
      setGrid(JSON.parse(Cookies.get("board")));
    }
    if (Cookies.get("stats")) {
      setStats(JSON.parse(Cookies.get("stats")));
    }
    setTurns(getCookieOrDefault("turns", 0));
    setNextTile(getCookieOrDefault("nextTile", 1));
    setClears(getCookieOrDefault("clears", 0));
    setGameOver(getCookieOrDefault("gameOver", false));
    setEndless(getCookieOrDefault("endless", false));
  }, []);

  return (
    <div className="App">
      <div id="center">
        <div id="title">rumm.io</div>
        <div id="subtitle" title="jpmchargue.com">a game by James</div>
        <div className="ui hud">
          <div id="next" className={`cell c${nextTile}`}>
            <div className={`cell-top c${nextTile}`}>{tileDisplayText(nextTile)}</div>
          </div>
          <div id="turns" className="hud-element" style={gameOver ? {fontSize: "50px"} : {fontSize: "30px"}}>{turns}</div>
          <div className="hud-element button" onClick={() => {setShowRules(true)}}>rules</div>
        </div>
        <div id="info-container">
          <div id="info-next-tile" className={`ui info ${showRules ? "fade-in" : ""}`}>
            next tile
          </div>
          <div id="info-turns" className={`ui info ${showRules ? "fade-in" : ""}`}>
            # turns
          </div>
          <div className={`ui info ${showRules ? "fade-in" : ""}`}>
            <div className="section">
              Click on an empty cell on the grid to place the current next tile.<br/>
              Tiles may be numbers between 1 and 6.
            </div>
            <div className="section">
              Clear rows and columns on the grid by forming <b>melds</b>.<br/>
              A meld can be a <b>set</b> of 5 of the same number:
            </div>
            {[4, 4, 4, 4, 4].map((cell, c) => (
              <div key={c} className={`cell c${cell}`}>
                <div key={c} className={`cell-top c${cell}`}>{cell ? cell : ""}</div>
              </div>
            ))}
            <div className="section">
              or a <b>run</b> of 5 consecutive numbers:
            </div>
            {[1, 3, 2, 5, 4].map((cell, c) => (
              <div key={c} className={`cell c${cell}`}>
                <div key={c} className={`cell-top c${cell}`}>{cell ? cell : ""}</div>
              </div>
            ))}
            <div className="section">
              The numbers in a run do not need to be in order.
            </div>
            <div className="section">
              New tiles will appear over time.<br/>
              <b>Form a meld of five 7s to win.</b>
            </div>
            <div className="button" style={{marginBottom: "2px"}} onClick={() => {setShowRules(false)}}>close</div>
          </div>
          <div className={`ui info win ${showWin ? "fade-in" : ""}`}>
            <div className="section win">
              YOU WON!
            </div>
            <div className="section header">
              Turn {turns}
            </div>
            <div className="grid win">
              {grid.map((row, r) => (
                <div key={r} className="row">
                  {row.map((cell, c) => (
                    <div key={(5*r)+c} className={`cell c${cell}`}>
                      <div key={(5*r)+c} className={`cell-top c${cell}`}>{tileDisplayText(cell)}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="section">
              Number of Wins: <b>{stats.historicWins}</b><br/>
              Average Win Turns: <b>{Math.round(stats.historicWinAvg*100)/100}</b>
            </div>
            <div className="ui button" onClick={() => copyResultString()}>{copiedToClipboard ? "copied" : "share results"}</div>
            <div className="button" style={{marginBottom: "2px"}} onClick={() => {startEndless()}}>continue</div>
          </div>
        </div>
        <div className="grid">
          {grid.map((row, r) => (
            <div key={r} className="row">
              {row.map((cell, c) => (
                <div key={(5*r)+c} className={`cell c${cell}`} onClick={() => placeTile(r, c)}>
                  <div key={(5*r)+c} className={`cell-top c${cell}`}>{tileDisplayText(cell)}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className={`ui section bottom-stats ${gameOver ? "fade-in" : ""}`}>
          Number of Runs: <b>{stats.historicGames}</b><br/>
          Average Run Turns: <b>{Math.round(stats.historicGameAvg*100)/100}</b>
        </div>
        <div className="ui button" onClick={() => restart()}>{"restart"}</div>
        {
          gameOver
          ? <div className="ui button" onClick={() => copyResultString()}>{copiedToClipboard ? "copied" : "share results"}</div>
          : <div></div>
        }
      </div>
    </div>
  );
}

export default App;
